/**
 * Logout helpers for createAuth
 * Extracted to reduce function size
 */

import { fetchWithNetworkLogging } from "@Front/Api/helpers/requestHelpers";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";

export interface LogoutUrlsParams {
	baseUrl: string;
}

/**
 * Builds logout URLs array
 */
export function buildLogoutUrls(params: LogoutUrlsParams): string[] {
	const { baseUrl } = params;
	return [`${baseUrl}/user/auth/logout`, `${baseUrl}/user/logout`, `${baseUrl}/logout`];
}

/**
 * Sends logout request to a single URL (tries POST first, then GET)
 */
export async function sendLogoutRequest(url: string, index: number, total: number): Promise<void> {
	try {
		const response = await fetchWithNetworkLogging(url, {
			method: "POST",
			credentials: "include",
			mode: "cors",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
			},
		});

		LoggerService.info(`✅ Requête POST IdNot ${index + 1}/${total} envoyée`, {
			url,
			status: response.status,
			statusText: response.statusText,
		});
	} catch (error) {
		try {
			const response = await fetchWithNetworkLogging(url, {
				method: "GET",
				credentials: "include",
				mode: "cors",
			});

			LoggerService.info(`✅ Requête GET IdNot ${index + 1}/${total} envoyée`, {
				url,
				status: response.status,
				statusText: response.statusText,
			});
		} catch (getError) {
			LoggerService.warn(`⚠️ Erreur lors de la requête IdNot ${index + 1}/${total}`, {
				url,
				postError: error,
				getError: getError,
				note: "On continue quand même, la déconnexion locale sera effectuée",
			});
		}
	}
}

/**
 * Implements logout from IdNot using fetch requests
 */
export async function logoutFromIdNotWithFetchImpl(baseUrl: string): Promise<void> {
	const logoutUrls = buildLogoutUrls({ baseUrl });

	LoggerService.info("🔄 Déconnexion IdNot via requêtes fetch avec credentials", {
		logoutUrls,
		baseUrl,
		note: "Les cookies IdNot seront révoqués via des requêtes fetch, puis on redirigera vers la home",
	});

	const fetchPromises = logoutUrls.map((url, index) => sendLogoutRequest(url, index, logoutUrls.length));

	try {
		await Promise.allSettled(fetchPromises);
		LoggerService.info("✅ Toutes les requêtes IdNot terminées", {
			total: logoutUrls.length,
			note: "Les cookies IdNot devraient être révoqués côté serveur IdNot",
		});
	} catch (error) {
		LoggerService.warn("⚠️ Erreur lors de l'envoi des requêtes IdNot", {
			error,
			note: "On continue quand même, la déconnexion locale sera effectuée",
		});
	}

	return new Promise((resolve) => {
		setTimeout(() => {
			LoggerService.info("✅ Déconnexion IdNot terminée", {
				note: "Délai de 1 seconde écoulé, les cookies IdNot devraient être révoqués",
			});
			resolve();
		}, 1000);
	});
}
